import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AuctionCard from "@/components/AuctionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Filter, X } from "lucide-react";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "estate", label: "Estate" },
  { value: "household", label: "Household" },
  { value: "tools", label: "Tools" },
  { value: "commercial", label: "Commercial" },
  { value: "collectibles", label: "Collectibles" },
  { value: "mixed", label: "Mixed / Unknown" },
];

const SORTS = [
  { value: "ending_soon", label: "Ending Soon" },
  { value: "newest", label: "Newest" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
  { value: "most_bids", label: "Most Active" },
];

export default function BrowsePage() {
  const location = useLocation();
  const nav = useNavigate();
  const params = new URLSearchParams(location.search);

  const [q, setQ] = useState(params.get("q") || "");
  const [state, setState] = useState(params.get("state") || "all");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [endingSoon, setEndingSoon] = useState(params.get("ending_soon") === "true");
  const [sort, setSort] = useState(params.get("sort") || "ending_soon");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchAuctions = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (state && state !== "all") p.set("state", state);
    if (category && category !== "all") p.set("category", category);
    if (endingSoon) p.set("ending_soon", "true");
    if (sort) p.set("sort", sort);
    p.set("limit", "60");
    try {
      const { data } = await api.get(`/auctions?${p.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q, state, category, endingSoon, sort]);

  useEffect(() => { fetchAuctions(); }, [fetchAuctions]);

  const clearFilters = () => {
    setQ(""); setState("all"); setCategory("all"); setEndingSoon(false); setSort("ending_soon");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">Live Marketplace</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
          Browse Storage Auctions
        </h1>
        <p className="text-sm text-zinc-500 mt-3">{total} unit{total === 1 ? "" : "s"} currently up for bid.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Filters */}
        <aside className="bg-zinc-950 border border-zinc-800 p-5 h-fit lg:sticky lg:top-24">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 text-orange-500">
              <Filter className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-[0.25em]">Filters</span>
            </div>
            <button
              data-testid="clear-filters"
              onClick={clearFilters}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-orange-500 flex items-center gap-1"
            >
              Clear <X className="w-3 h-3" />
            </button>
          </div>

          <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">Search</label>
          <div className="relative mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="filter-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Keyword…"
              className="pl-9 rounded-none bg-black border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input"
            />
          </div>

          <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">State</label>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="filter-state" className="rounded-none bg-black border-zinc-800 mb-5">
              <SelectValue placeholder="Any state" />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-zinc-950 border-zinc-800 max-h-72">
              <SelectItem value="all">Any State</SelectItem>
              {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">Category</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="filter-category" className="rounded-none bg-black border-zinc-800 mb-5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
              {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <label className="flex items-center gap-3 cursor-pointer mb-2 select-none">
            <Checkbox
              data-testid="filter-ending-soon"
              checked={endingSoon}
              onCheckedChange={(v) => setEndingSoon(!!v)}
              className="rounded-none border-zinc-700 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-500"
            />
            <span className="text-sm text-zinc-300">Ending in 24 hours</span>
          </label>
        </aside>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
              Showing {items.length} of {total}
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="w-56 rounded-none bg-zinc-950 border-zinc-800 text-xs uppercase tracking-widest">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="aspect-[4/3] bg-zinc-900 border border-zinc-800 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-zinc-800">
              <div className="font-display text-2xl uppercase tracking-tight text-zinc-500">No auctions match your filters</div>
              <Button onClick={clearFilters} variant="ghost" className="rounded-none mt-4 text-orange-500 hover:bg-zinc-900">Reset filters</Button>
            </div>
          ) : (
            <div data-testid="auction-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
              {items.map((a, i) => <AuctionCard key={a.id} auction={a} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
